import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'

const DemoModeContext = createContext(undefined)

/**
 * Manages demo mode state and persists preference to localStorage
 * Adds/removes 'demo-mode' class on document root to trigger CSS blur
 * Tracks when conversation views (with PII data) are active
 */
export const DemoModeProvider = ({ children }) => {
  const [isDemoMode, setIsDemoMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('demoMode') === 'true'
    }
    return false
  })

  // Track active conversation views (MessageBody instances)
  const conversationViewCount = useRef(0)
  const [isConversationViewActive, setIsConversationViewActive] = useState(false)

  useEffect(() => {
    const root = window.document.documentElement
    if (isDemoMode) {
      root.classList.add('demo-mode')
    } else {
      root.classList.remove('demo-mode')
    }
    localStorage.setItem('demoMode', String(isDemoMode))
  }, [isDemoMode])

  const toggleDemoMode = () => setIsDemoMode((prev) => !prev)

  // Register/unregister conversation views (called by MessageBody on mount/unmount)
  const registerConversationView = useCallback(() => {
    conversationViewCount.current += 1
    setIsConversationViewActive(true)

    return () => {
      conversationViewCount.current -= 1
      if (conversationViewCount.current === 0) {
        setIsConversationViewActive(false)
      }
    }
  }, [])

  return (
    <DemoModeContext.Provider value={{
      isDemoMode,
      toggleDemoMode,
      isConversationViewActive,
      registerConversationView
    }}>
      {children}
    </DemoModeContext.Provider>
  )
}

export const useDemoMode = () => {
  const context = useContext(DemoModeContext)
  if (context === undefined) {
    throw new Error('useDemoMode must be used within a DemoModeProvider')
  }
  return context
}
